import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { object, string } from 'yup';

import PatientSourceApi from '../../../../../api/patient/PatientSourceApi';
import ModalBoxes, { useModalBoxes } from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../common/general/Button';
import ButtonGroup from '../../../../../common/general/ButtonGroup';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { onRequestError } from '../../../../../services/handlers';
import { caseInsensitiveStringEquals } from '../../../../../services/string';

const sourceTypes = ['API', 'Advertising', 'Referral', 'Search', 'Social Media', 'Other'];

export default function AddPatientSourceModal({ sources }) {
  const { setLoading, close } = useModalBoxes();
  const { submitForm, handleSubmit, handleChange, handleBlur, values, touched, errors } = useFormik({
    initialValues: {
      source: '',
      type: ''
    },
    validationSchema: createValidationSchema(sources),
    onSubmit: values => {
      ModalBoxes.confirm({
        content: `Are you sure you want to create the patient source "${values.source}"?\nPlease note that once this patient source is added, it cannot be deleted.`,
        size: 'w500'
      }).then(
        function() {
          setLoading(true);
          PatientSourceApi.createPatientSource(values)
            .then(function({ data }) {
              close(data);
              NotificationManager.success(`Patient source "${data.source}" added successfully`);
            }, onRequestError)
            .finally(function() {
              setLoading(false);
            });
        },
        () => {}
      );
    }
  });

  return (
    <>
      <ModalBoxes.Header>Add Patient Source</ModalBoxes.Header>
      <ModalBoxes.Body>
        <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                name="source"
                value={values.source}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.source && Boolean(errors.source)}
                helperText={touched.source && errors.source}
                fullWidth
                label="Patient Source Name"
                maxRows={4}
                variant="outlined"
                slotProps={{
                  htmlInput: { maxLength: 255 }
                }}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="type"
                value={values.type}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.type && Boolean(errors.type)}
                helperText={touched.type && errors.type}
                fullWidth
                select
                label="Type"
                variant="outlined"
                required
              >
                {sourceTypes.map(name => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </ModalBoxes.Body>
      <ModalBoxes.Footer>
        <ButtonGroup>
          <Button priority="medium" onClick={close}>
            Cancel
          </Button>
          <Button onClick={submitForm}>Add</Button>
        </ButtonGroup>
      </ModalBoxes.Footer>
    </>
  );
}

function createValidationSchema(sources) {
  return object({
    source: string()
      .trim()
      .required('Source is required')
      .test('unique', 'Patient source with this name already exists', value => {
        if (!value || !sources) return true;
        return !sources.some(s => caseInsensitiveStringEquals(s.source, value));
      }),
    type: string().required('Type is required')
  });
}

AddPatientSourceModal.size = 'w500';
